import assign from "lodash.assign";
import find from "lodash.find";
import { browserHistory } from "react-router";
import appPath from "utils/appPath";
import { SCREENS, ABBA_CONFIGS } from "common/constants/Constants";
import Entitlements from "services/Entitlements";
import localStorageUtil from "utils/localStorageUtil";
import { findCurrentSubscriptionWithDetails } from "common/utils/subscriptionHelpers";

export const SUBSCRIPTION_OFFERS_KEY = "subscriptionOffers";

// Actions (must be name-spaced for reducer)
export const OFFER_SET_CURRENT_RATE_PLAN = "OFFER_SET_CURRENT_RATE_PLAN";
export const OFFER_SET_OFFER_RATE_PLAN = "OFFER_SET_OFFER_RATE_PLAN";
export const OFFER_SET_SUBSCRIPTION = "OFFER_SET_SUBSCRIPTION";
export const OFFER_SET_OFFERS = "OFFER_SET_OFFERS";

// Reducer
const DEFAULT_STATE = {
    currentRatePlan: {},
    offerRatePlan: {},
    subscription: {},
    allOffers: []
};

export function offerReducer(state = DEFAULT_STATE, action) {
    switch (action.type) {
        case OFFER_SET_CURRENT_RATE_PLAN:
            return assign({}, state, {
                currentRatePlan: action.currentRatePlan
            });
        case OFFER_SET_OFFER_RATE_PLAN:
            return assign({}, state, {
                offerRatePlan: action.offerRatePlan
            });
        case OFFER_SET_SUBSCRIPTION:
            return assign({}, state, {
                subscription: action.subscription
            });
        case OFFER_SET_OFFERS:
            return assign({}, state, {
                allOffers: action.allOffers
            });
        default:
            return state;
    }
}

// Action Creators
export function setCurrentRatePlan(currentRatePlan) {
    return {
        type: OFFER_SET_CURRENT_RATE_PLAN,
        currentRatePlan
    };
}

export function setOfferRatePlan(offerRatePlan) {
    return {
        type: OFFER_SET_OFFER_RATE_PLAN,
        offerRatePlan
    };
}

export function setSubscription(subscription) {
    return {
        type: OFFER_SET_SUBSCRIPTION,
        subscription
    };
}

export function setOffers(offers) {
    return {
        type: OFFER_SET_OFFERS,
        allOffers: offers
    };
}

export function fetchCurrentRatePlanInformation() {
    return (dispatch, getState) => {
        const {
            offer: {
                subscription: {
                    ratePlan: {
                        ratePlanId
                    } = {}
                }
            }
        } = getState();

        return Entitlements.getProductRatePlanInformation(ratePlanId)
            .then(({ ratePlan }) => dispatch(setCurrentRatePlan(ratePlan)))
            .catch((error) => {
                browserHistory.push(appPath(SCREENS.OFFERS, { search: "" }));

                throw error;
            });
    };
}

export function fetchOfferRatePlanInformation(ratePlanId) {
    return (dispatch, getState) => {
        return new Promise((resolve, reject) => {
            const {
                abba: {
                    config: {
                        [ABBA_CONFIGS.UPSELL_OFFER]: {
                            ratePlanId: abbaRatePlanId
                        } = {}
                    } = {}
                } = {}
            } = getState();

            if (abbaRatePlanId !== ratePlanId) {
                return reject();
            }

            return resolve();
        })
            .then(() => Entitlements.getProductRatePlanInformation(ratePlanId))
            .then(({ ratePlan }) => dispatch(setOfferRatePlan(ratePlan)))
            .catch((error) => {
                browserHistory.push(appPath(SCREENS.OFFERS, { search: "" }));

                throw error;
            });
    };
}

function fetchSubscriptionsIfMissing() {
    return (dispatch, getState) => {
        const { entitlements : { subscriptions } = {} } = getState();

        const currentSubscription = findCurrentSubscriptionWithDetails(subscriptions);

        return new Promise((resolve, reject) => {
            if (currentSubscription) {
                return resolve(currentSubscription);
            }

            return Entitlements.getSubscriptions({ showAccounts: true, showRatePlans: true, activeOnly: true })
                .then(findCurrentSubscriptionWithDetails)
                .then(resolve)
                .catch(reject);
        }).then((subscription) => dispatch(setSubscription(subscription)));
    };
}

export function addOffer(offer) {
    return (dispatch) => {
        const offers = localStorageUtil.getObject(SUBSCRIPTION_OFFERS_KEY, []);
        const offerAlreadyExists = offers.some((existingOffer) => {
            return existingOffer.ratePlanId === offer.ratePlanId;
        });

        if (!offerAlreadyExists) {
            offers.push(offer);
            localStorageUtil.setObject(SUBSCRIPTION_OFFERS_KEY, offers);
        }

        dispatch(setOffers(offers));
    };
}

export function clearOffersNotification() {
    return (dispatch) => {
        const offers = localStorageUtil.getObject(SUBSCRIPTION_OFFERS_KEY, []);

        if (offers.length === 0) {
            return;
        }

        offers.forEach((offer) => {
            offer.seen = true;
        });

        dispatch(setOffers(offers));
        localStorageUtil.setObject(SUBSCRIPTION_OFFERS_KEY, offers);
    };
}

export function clearSpecificOfferNotification(ratePlanId) {
    return (dispatch) => {
        const existingOffers = localStorageUtil.getObject(SUBSCRIPTION_OFFERS_KEY, []);
        const matchingOffer = find(existingOffers, { ratePlanId });

        if (!matchingOffer) {
            return;
        }

        matchingOffer.seen = true;

        dispatch(setOffers(existingOffers));
        localStorageUtil.setObject(SUBSCRIPTION_OFFERS_KEY, existingOffers);
    };
}

export default {
    setCurrentRatePlan,
    setOfferRatePlan,
    setSubscription,
    setOffers,
    fetchCurrentRatePlanInformation,
    fetchOfferRatePlanInformation,
    fetchSubscriptionsIfMissing,
    addOffer,
    clearOffersNotification,
    clearSpecificOfferNotification
};
